// file: DriveContext.tsx
import React, { createContext, useState, useContext } from "react";
import { PaginationDto } from "../interfaces/Employee/Employee";

// The type for our view mode
export type ViewMode = "grid" | "table";

// The shape of the context value
interface DriveContextValue {
  viewMode: ViewMode;
  setViewMode: React.Dispatch<React.SetStateAction<ViewMode>>;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  onFileUploadClick?: () => void;
  setOnFileUploadClick?: React.Dispatch<React.SetStateAction<(() => void) | undefined>>;
  onFolderCreateClick?: () => void;
  setOnFolderCreateClick?: React.Dispatch<React.SetStateAction<(() => void) | undefined>>;
  // Pagination state
  pagination: PaginationDto;
  setPagination: React.Dispatch<React.SetStateAction<PaginationDto>>;
  // Total items count for pagination
  totalItems: number;
  setTotalItems: React.Dispatch<React.SetStateAction<number>>;
  // Move functionality
  selectedFileIds: string[];
  setSelectedFileIds: React.Dispatch<React.SetStateAction<string[]>>;
  showMoveModal: boolean;
  setShowMoveModal: React.Dispatch<React.SetStateAction<boolean>>;
  refreshCurrentFolder?: () => void;
  setRefreshCurrentFolder: React.Dispatch<React.SetStateAction<(() => void) | undefined>>;
  // Current folder ID for move validation
  currentFolderId: number | null;
  setCurrentFolderId: React.Dispatch<React.SetStateAction<number | null>>;
}

// Create the context with a default placeholder
const DriveContext = createContext<DriveContextValue | undefined>(undefined);

/**
 * Provides the Drive context (viewMode & setter, searchTerm & setter) to child components.
 */
export const DriveProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // The actual state lives here
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [onFileUploadClick, setOnFileUploadClick] = useState<(() => void) | undefined>(undefined);
  const [onFolderCreateClick, setOnFolderCreateClick] = useState<(() => void) | undefined>(undefined);
  const [pagination, setPagination] = useState<PaginationDto>({
    pageNumber: 1,
    pageSize: 10,
  });
  const [totalItems, setTotalItems] = useState<number>(0);
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
  const [showMoveModal, setShowMoveModal] = useState<boolean>(false);
  const [refreshCurrentFolder, setRefreshCurrentFolder] = useState<(() => void) | undefined>(undefined);
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);

  return (
    <DriveContext.Provider value={{ 
      viewMode, 
      setViewMode, 
      searchTerm, 
      setSearchTerm,
      onFileUploadClick,
      setOnFileUploadClick,
      onFolderCreateClick,
      setOnFolderCreateClick,
      pagination,
      setPagination,
      totalItems,
      setTotalItems,
      selectedFileIds,
      setSelectedFileIds,
      showMoveModal,
      setShowMoveModal,
      refreshCurrentFolder,
      setRefreshCurrentFolder,
      currentFolderId,
      setCurrentFolderId
    }}>
      {children}
    </DriveContext.Provider>
  );
};

/**
 * Custom hook to read the DriveContext
 */
export function useDriveContext(): DriveContextValue {
  const context = useContext(DriveContext);
  if (!context) {
    throw new Error("useDriveContext must be used within a DriveProvider");
  }
  return context;
}
